import React from 'react';
import { css, cx } from 'linaria';
import { Query } from 'react-apollo';
import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import CategoryLink from '@jetshop/ui/CategoryLink';
import { Below } from '@jetshop/ui/Breakpoints';
import homeCategoriesQuery from '../../Layout/Header/HomeCategoriesQuery.gql';
import { theme } from '../../Theme';
import { containerClass, pseudoCaretCSS } from '../ThemeClasses';

const sidebarBreakpointKey = 'lg';

/* categoryWithSibebarCSS is also used for sidebar loading state  */
const categoryWithSibebarCSS = css`
  padding-left: 0;
  padding-right: 0;
  max-width: none;
  min-height: 100vh;

  &__sidebar {
    display: none;
  }

  ${theme.above[sidebarBreakpointKey]} {
    display: flex;

    &__sidebar {
      flex: 0 0 25%;
      display: block;
      padding: ${theme.space[4]};
      margin-top: -${theme.elementSizes.pagePaddingTop};
      margin-bottom: -${theme.elementSizes.pagePaddingBottom};
      background: ${theme.colors.white};
      z-index: 2;
    }

    &__content {
      flex: 0 1 75%;
      margin-top: ${theme.space[1]};
      padding-left: ${theme.space[7]};
      overflow-x: hidden;
      padding-right: 2rem;
    }
  }
`;

export const categoryNavigationCSS = css`
  color: ${theme.colors.black};

  a {
    text-decoration: none;
    color: currentColor;
  }

  &__title {
    margin-bottom: ${theme.space[4]};
    font-size: 1.625rem;
  }

  &__level {
    margin-top: ${theme.space[1]};

    &--one {
      margin-top: 0;
    }
  }

  &__list-item {
    margin-bottom: 28px;

    &:last-child,
    ul ul & {
      margin-bottom: 0;
    }
  }

  &__link-item {
    display: block;

    ul ul & {
      padding: 8px 14px;
      margin-top: ${theme.space[1]};
      font-size: 1rem;
      font-weight: 400;
      background: transparent;
      transition: background 0.1s ease-in-out, font-weight ease-in-out 0.1s;

      &.active,
      &:hover {
        background: ${theme.colors.grey600};
        text-decoration: none;

        border-radius: 4px;
      }

      &.active {
      }
    }

    &.active {
      text-decoration: underline;
    }
  }

  &__link-item,
  &__button {
    font-size: 1.125rem;
    line-height: 1.25;
    font-weight: 700;

    &:hover {
      text-decoration: underline;
    }
  }

  &__button {
    width: 100%;
    border: 0;
    background: transparent;
  }

  &__button-text {
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 700;
    font-size: 1rem;

    &:after {
      transform: rotate(0);
      transition: transform ease-in-out 0.2s;
    }

    &--open {
      &:after {
        transform: rotate(90deg);
      }
    }
  }
`;

const CategoryAccordionItem = ({
  category,
  activeCategoryParentId,
  handleClick,
  AccordionContent,
  openIndexes,
  index
}) => {
  return (
    <li className={`${categoryNavigationCSS}__list-item`}>
      {category?.hasSubcategories ? (
        <>
          <button
            className={`${categoryNavigationCSS}__button`}
            onClick={() => handleClick(index)}
          >
            <span
              className={cx(
                `${categoryNavigationCSS}__button-text`,
                openIndexes.includes(index)
                  ? `${categoryNavigationCSS}__button-text--open`
                  : null,
                pseudoCaretCSS
              )}
            >
              {category.name}
            </span>
          </button>
          <AccordionContent isOpen={openIndexes.includes(index)}>
            <CategoryLevel
              category={category}
              activeCategoryParentId={activeCategoryParentId}
            >
              <li className={`${categoryNavigationCSS}__list-item`}>
                <CategoryLink
                  exact
                  className={cx(
                    `${categoryNavigationCSS}__link-item`,
                    `${categoryNavigationCSS}__link-item--main`
                  )}
                  category={category}
                >
                  {t('Everything in {categoryName}', {
                    categoryName: category.name
                  })}
                </CategoryLink>
              </li>
            </CategoryLevel>
          </AccordionContent>
        </>
      ) : (
        <CategoryLink
          className={`${categoryNavigationCSS}__link-item`}
          category={category}
        >
          {category.name}
        </CategoryLink>
      )}
    </li>
  );
};

const CategoryLevel = ({
  children,
  category,
  activeCategoryParentId,
  level
}) => {
  return category?.subcategories?.length > 0 ? (
    <ul
      className={cx(
        `${categoryNavigationCSS}__level`,
        level ? `${categoryNavigationCSS}__level-${level}` : null
      )}
    >
      {children}
      {category.subcategories.map((subcategory, i) => (
        <Accordion
          key={i}
          initialOpenIndexes={
            subcategory.id === activeCategoryParentId ? [i] : null
          }
        >
          {({ openIndexes, handleClick, AccordionContent }) => (
            <CategoryAccordionItem
              category={subcategory}
              activeCategoryParentId={activeCategoryParentId}
              handleClick={handleClick}
              AccordionContent={AccordionContent}
              openIndexes={openIndexes}
              index={i}
            />
          )}
        </Accordion>
      ))}
    </ul>
  ) : null;
};

export const CategoryWithSidebarContainer = ({ children }) => {
  return (
    <Below breakpoint={sidebarBreakpointKey}>
      {matches =>
        matches ? (
          children
        ) : (
          <div className={cx(categoryWithSibebarCSS, containerClass)}>
            {children}
          </div>
        )
      }
    </Below>
  );
};

const SidebarChildren = ({ children }) => {
  return (
    <div className={cx(`${categoryWithSibebarCSS}__sidebar`, 'side-bar')}>
      {children}
    </div>
  );
};

export const CategoryWithSidebarInner = ({ children }) => {
  return children(SidebarChildren);
};

export const CategoryWithSidebarContent = ({ children }) => {
  return (
    <div className={cx(`${categoryWithSibebarCSS}__content`, 'content')}>
      {children}
    </div>
  );
};

const CategoryWithSidebar = ({ children, category }) => {
  const topLevelCategory = category?.parent?.parent
    ? category.parent.parent
    : category?.parent
    ? category.parent
    : category;

  if (!topLevelCategory) return children;

  const activeCategoryParentId = category?.parent?.parent?.id
    ? category.parent.id
    : category?.parent?.id
    ? category.id
    : null;

  const topLevelCategoryId = topLevelCategory?.id;

  return (
    <Query query={homeCategoriesQuery} variables={{ levels: 2 }}>
      {({ data, loading, error }) => {
        if (loading || error) return children;

        const topLevelCategory = data?.categories?.find(
          cat => cat.id === topLevelCategoryId
        );

        return !topLevelCategory?.hasSubcategories ? (
          children
        ) : (
          <CategoryWithSidebarContainer>
            <CategoryWithSidebarInner>
              {SidebarChildren => (
                <>
                  <SidebarChildren>
                    <div className={categoryNavigationCSS}>
                      <h2 className={`${categoryNavigationCSS}__title`}>
                        <CategoryLink category={topLevelCategory}>
                          {topLevelCategory.name}
                        </CategoryLink>
                      </h2>
                      <CategoryLevel
                        category={topLevelCategory}
                        activeCategoryParentId={activeCategoryParentId}
                        level={'one'}
                      />
                    </div>
                  </SidebarChildren>
                  <CategoryWithSidebarContent>
                    {children}
                  </CategoryWithSidebarContent>
                </>
              )}
            </CategoryWithSidebarInner>
          </CategoryWithSidebarContainer>
        );
      }}
    </Query>
  );
};

export default CategoryWithSidebar;
